import { lazy, Suspense } from "react";
import { Link, Navigate, Outlet, Route, Routes } from "react-router-dom";

import { AppShell } from "@/components/AppShell";
import { AdminOrLeadOnly } from "@/components/AdminOrLeadOnly";
import { AdminOnly } from "@/components/AdminOnly";
import { RequireAuth } from "@/components/RequireAuth";
import { HomeRoute, RequireDataAccess } from "@/components/RequireDataAccess";
import { Spinner } from "@/components/Spinner";

const LoginPage = lazy(() =>
  import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage }))
);
const OverviewPage = lazy(() =>
  import("@/pages/OverviewPage").then((m) => ({ default: m.OverviewPage }))
);
const XianPage = lazy(() =>
  import("@/pages/XianPage").then((m) => ({ default: m.XianPage }))
);
const XianVersusPage = lazy(() =>
  import("@/pages/XianVersusPage").then((m) => ({ default: m.XianVersusPage }))
);
const PlayersPage = lazy(() =>
  import("@/pages/PlayersPage").then((m) => ({ default: m.PlayersPage }))
);
const PlayerDetailPage = lazy(() =>
  import("@/pages/PlayerDetailPage").then((m) => ({ default: m.PlayerDetailPage }))
);
const ReferrersPage = lazy(() =>
  import("@/pages/ReferrersPage").then((m) => ({ default: m.ReferrersPage }))
);
const GamesPage = lazy(() =>
  import("@/pages/GamesPage").then((m) => ({ default: m.GamesPage }))
);
const DomainAnalyticsPage = lazy(() =>
  import("@/pages/DomainAnalyticsPage").then((m) => ({ default: m.DomainAnalyticsPage }))
);
const MarketingPage = lazy(() =>
  import("@/pages/MarketingPage").then((m) => ({ default: m.MarketingPage }))
);
const SyncPage = lazy(() =>
  import("@/pages/SyncPage").then((m) => ({ default: m.SyncPage }))
);
const SettingsPage = lazy(() =>
  import("@/pages/SettingsPage").then((m) => ({ default: m.SettingsPage }))
);
const AdAccountsPage = lazy(() =>
  import("@/pages/AdAccountsPage").then((m) => ({ default: m.AdAccountsPage }))
);
const AdLabPage = lazy(() =>
  import("@/pages/AdLabPage").then((m) => ({ default: m.AdLabPage }))
);
const AdminUsersPage = lazy(() =>
  import("@/pages/AdminUsersPage").then((m) => ({ default: m.AdminUsersPage }))
);
const TelegramReportsPage = lazy(() =>
  import("@/pages/TelegramReportsPage").then((m) => ({ default: m.TelegramReportsPage }))
);
const StatusOptionsSettingsPage = lazy(() =>
  import("@/pages/StatusOptionsSettingsPage").then((m) => ({ default: m.StatusOptionsSettingsPage }))
);
const MyAccountPage = lazy(() =>
  import("@/pages/MyAccountPage").then((m) => ({ default: m.MyAccountPage }))
);
const TeamBrandTargetsPage = lazy(() =>
  import("@/pages/TeamBrandTargetsPage").then((m) => ({ default: m.TeamBrandTargetsPage }))
);

function NotFound() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8">
      <h1 className="text-xl font-semibold text-slate-900">ไม่พบหน้า</h1>
      <Link
        to="/"
        className="rounded-lg border border-border bg-surface-raised px-4 py-2 text-sm text-accent hover:bg-surface-overlay"
      >
        หน้าแรก
      </Link>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <Suspense fallback={<Spinner className="min-h-screen" />}>
            <LoginPage />
          </Suspense>
        }
      />
      <Route
        element={
          <RequireAuth>
            <Outlet />
          </RequireAuth>
        }
      >
        <Route path="/" element={<AppShell />}>
          {/* หน้าข้อมูล: คนไม่มีสิทธิ์ (user / marketing ไม่มีทีม) เห็นหน้าแจ้งแทน · user ที่มีทีมเข้า / แล้วไป บัญชีโฆษณา 2 */}
          <Route index element={<HomeRoute><OverviewPage /></HomeRoute>} />
          <Route path="xian" element={<RequireDataAccess><XianPage /></RequireDataAccess>} />
          <Route path="xian/vs" element={<RequireDataAccess><XianVersusPage /></RequireDataAccess>} />
          {/* หน้ารายงานรายวัน + การเงิน ถูกลบ 2026-08-23 (เลิกใช้ — รอหน้าการเงินรูปแบบใหม่)
              API /api/dashboard/xian-daily-report + /api/xian/daily/finance/* และข้อมูลยังอยู่ครบ */}
          <Route path="xian/daily/*" element={<Navigate to="/xian" replace />} />
          <Route path="players" element={<RequireDataAccess><PlayersPage /></RequireDataAccess>} />
          <Route path="players/:id" element={<RequireDataAccess><PlayerDetailPage /></RequireDataAccess>} />
          <Route path="referrers" element={<RequireDataAccess><ReferrersPage /></RequireDataAccess>} />
          {/* วิเคราะห์โดเมน/ref = tab ใต้ผู้แนะนำ (เดิมอยู่ที่ /domains) */}
          <Route path="referrers/domains" element={<RequireDataAccess><DomainAnalyticsPage /></RequireDataAccess>} />
          <Route path="games" element={<RequireDataAccess><GamesPage /></RequireDataAccess>} />
          {/* Redirect bookmark เก่า — หน้า salepage CMS ถูกลบ (ซ้ำระบบ kkautoweb) */}
          <Route path="salepages" element={<Navigate to="/" replace />} />
          <Route path="salepages/build" element={<Navigate to="/" replace />} />
          <Route path="salepages/:id/builder" element={<Navigate to="/" replace />} />
          <Route path="landing-pages" element={<Navigate to="/" replace />} />
          <Route path="landing-pages/:id/builder" element={<Navigate to="/" replace />} />
          {/* /domains ย้ายเป็น tab ใต้ /referrers */}
          <Route path="domains" element={<Navigate to="/referrers/domains" replace />} />
          <Route path="ad-accounts" element={<AdAccountsPage />} />
          {/* บัญชีโฆษณา 2 = ระบบใหม่ (native) ชี้ข้อมูล kk จริง · ของเดิม (iframe) ถูกลบออกแล้ว */}
          <Route path="ad-accounts2/*" element={<AdLabPage />} />
          <Route path="ad-accounts2-old/*" element={<Navigate to="/ad-accounts2" replace />} />
          {/* บัญชีของฉัน — ทุก role (เปิดจากชื่อมุมขวาบน) */}
          <Route path="me" element={<MyAccountPage />} />
          <Route
            path="admin/users"
            element={
              <AdminOnly>
                <AdminUsersPage />
              </AdminOnly>
            }
          />
          <Route
            path="admin/telegram-reports"
            element={
              <AdminOnly>
                <TelegramReportsPage />
              </AdminOnly>
            }
          />
          {/* /team ถูกลบ (data ก้อนเดียวกับ /xian) — redirect bookmark เก่า */}
          <Route path="team" element={<Navigate to="/xian" replace />} />
          <Route
            path="marketing"
            element={
              <AdminOnly>
                <MarketingPage />
              </AdminOnly>
            }
          />
          <Route
            path="sync"
            element={
              <AdminOnly>
                <SyncPage />
              </AdminOnly>
            }
          />
          <Route
            path="settings"
            element={
              <AdminOnly>
                <SettingsPage />
              </AdminOnly>
            }
          />
          <Route
            path="settings/statuses"
            element={
              <AdminOrLeadOnly>
                <StatusOptionsSettingsPage />
              </AdminOrLeadOnly>
            }
          />
          <Route
            path="settings/team-targets"
            element={
              <AdminOnly>
                <TeamBrandTargetsPage />
              </AdminOnly>
            }
          />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
